/**
 * Synchronous feature detection for selecting the in-browser classifier UI
 * or the legacy eight-slot fallback before the model code loads.
 * It performs no network or asynchronous work, so callers can use it during render.
 *
 * We do NOT detect threads/SharedArrayBuffer: we deliberately run single-thread
 * WASM-SIMD with no cross-origin isolation (see ma-frontend-browser-support.md).
 */

// A small WASM module that uses a v128 (SIMD) instruction. `WebAssembly.validate`
// returns false on engines without SIMD support. Source: GoogleChromeLabs/wasm-feature-detect.
const SIMD_PROBE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15,
  253, 98, 11,
]);

function hasWasmSimd(): boolean {
  try {
    return typeof WebAssembly === "object" && WebAssembly.validate(SIMD_PROBE);
  } catch {
    return false;
  }
}

export interface CapabilityReport {
  supported: boolean;
  wasm: boolean;
  simd: boolean;
  createImageBitmap: boolean;
  offscreenCanvas: boolean;
  /** WebGPU present — informational only; not required, never relied upon. */
  webgpu: boolean;
}

/** The three probes behind `supported`, for the console and the reporter. */
export function describeCapability(report: CapabilityReport): string {
  return `wasm=${report.wasm} simd=${report.simd} createImageBitmap=${report.createImageBitmap}`;
}

/**
 * Whether the browser can run the in-browser classifier at all. The latency gate
 * (a timed warmup) is a separate, runtime check applied after the model loads.
 */
export function detectCapability(): CapabilityReport {
  const wasm = typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function";
  const simd = wasm && hasWasmSimd();
  const cib = typeof createImageBitmap === "function";
  // OffscreenCanvas speeds preprocessing but is not strictly required — a 2D <canvas>
  // works too. We still report it so the preprocessor can choose the faster path.
  const osc = typeof OffscreenCanvas === "function";
  const webgpu = typeof navigator !== "undefined" && "gpu" in navigator;
  return {
    supported: wasm && simd && cib,
    wasm,
    simd,
    createImageBitmap: cib,
    offscreenCanvas: osc,
    webgpu,
  };
}
